// @mui
import {
  Avatar,
  Box,
  Button,
  Card,
  Divider,
  Link,
  Stack,
  Typography,
  Container,
  Grid,
} from '@mui/material';
// ----------------------------------------------------------------------
import { alpha, styled } from '@mui/material/styles';
import SvgColor from 'components/svg-color';
import DashboardLayout from 'layouts/dashboard';

import Image from 'components/image';
import { useSettingsContext } from 'components/settings';
import axiosInstance from 'utils/axios';
import { IConfig } from '../../@types/config';
import { GetServerSidePropsContext } from 'next';
import uuidv4 from 'utils/uuidv4';

const StyledOverlay = styled('div')(({ theme }) => ({
  top: 0,
  left: 0,
  zIndex: 8,
  width: '100%',
  height: '100%',
  position: 'absolute',
  backgroundColor: alpha(theme.palette.grey[900], 0.64),
}));
Support.getLayout = (page: React.ReactElement) => <DashboardLayout>{page}</DashboardLayout>;

export interface ItemSupportValue {
  title: string;
  link: string;
  content: string;
  linkIcon: string;
  linkBackGround: string;
}

export default function Support({ configs }: { configs: IConfig[] }) {
  const { themeStretch } = useSettingsContext();

  return (
    <Container maxWidth={themeStretch ? false : 'lg'}>
      <Grid container justifyContent="center" alignItems="center" spacing={2}>
        {configs.map((item, index) => {
          let content: ItemSupportValue | null = null;
          try {
            content = JSON.parse(item.value);
          } catch (e) {
            //
          }
          if (!content) return null;

          return (
            <Grid item xs={12} md={4} key={uuidv4()}>
              <Link
                rel="noopener noreferrer"
                underline="none"
                variant="caption"
                sx={{
                  cursor: 'pointer',
                  color: 'text.secondary',
                  transition: (theme) => theme.transitions.create('all'),
                  '&:hover': { color: 'primary.main' },
                }}
                href={content?.link}
                target="_blank"
              >
                <Card style={{ textAlign: 'center' }}>
                  <Box sx={{ position: 'relative' }}>
                    <SvgColor
                      src="/assets/shape_avatar.svg"
                      sx={{
                        width: 144,
                        height: 62,
                        zIndex: 10,
                        left: 0,
                        right: 0,
                        bottom: -26,
                        mx: 'auto',
                        position: 'absolute',
                        color: 'background.paper',
                      }}
                    />

                    <Avatar
                      alt={'name'}
                      src={content?.linkIcon}
                      sx={{
                        width: 64,
                        height: 64,
                        zIndex: 11,
                        left: 0,
                        right: 0,
                        bottom: -32,
                        mx: 'auto',
                        position: 'absolute',
                      }}
                    />

                    <StyledOverlay />

                    <Image src={content?.linkBackGround} alt={'cover'} ratio="16/9" />
                  </Box>

                  <Typography variant="subtitle1" sx={{ mt: 6, mb: 0.5 }}>
                    {content?.title}
                  </Typography>

                  <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="center"
                    sx={{ mt: 1, mb: 3 }}
                  ></Stack>

                  <Divider sx={{ borderStyle: 'dashed' }} />
                  <Typography variant="body2" sx={{ color: 'text.secondary', mt: 2, pl: 1, pr: 1 }}>
                    {content?.content}
                  </Typography>
                  <Button sx={{ mt: 1, mb: 1 }} variant="outlined">
                    Truy cập ngay
                  </Button>
                  <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="center"
                    sx={{ mt: 1, mb: 3 }}
                  ></Stack>
                </Card>
              </Link>
            </Grid>
          );
        })}
      </Grid>
    </Container>
  );
}

export async function getServerSideProps({ res, req }: GetServerSidePropsContext) {
  res.setHeader('Cache-Control', 'public, s-maxage=3600, stale-while-revalidate=60');
  try {
    const cookies = req?.headers.cookie ? `${req.headers.cookie};` : '';
    const config = {
      headers: {
        Cookie: cookies,
      },
    };
    const { data } = await axiosInstance.get<IConfig[]>(
      `/api/v1/config/${req.headers.host}/support`,
      config
    );

    return { props: { configs: data } };
  } catch (e) {
    if ([401, 403].includes(e?.status)) {
      return {
        redirect: {
          destination: '/404',
          permanent: false,
        },
      };
    }
    return { props: { configs: [], errors: e.errors } };
  }
}
